import { inject, Injectable } from '@angular/core';
import { from, map, switchMap, throwError, type Observable } from 'rxjs';
import { FilesApiService } from '../../core/api/files-api.service';

export interface KnowledgeUploadedFile {
  fileId: string;
  contentType: string;
  filename: string;
  sizeBytes: number;
}

@Injectable({ providedIn: 'root' })
export class KnowledgeFileUploadService {
  private readonly filesApi = inject(FilesApiService);

  upload(file: File): Observable<KnowledgeUploadedFile> {
    const contentType = normalizeKnowledgeContentType(file);

    return this.filesApi
      .issueUploadUrl({
        module: 'knowledge',
        contentType,
        filename: file.name,
        sizeBytes: file.size,
      })
      .pipe(
        switchMap((issued) =>
          from(fetch(issued.uploadUrl, { method: 'PUT', headers: issued.requiredHeaders, body: file })).pipe(
            switchMap((response) =>
              response.ok
                ? this.filesApi.confirmUpload(issued.fileId)
                : throwError(() => new Error(`upload failed with status ${response.status}`)),
            ),
            map(() => ({
              fileId: issued.fileId,
              contentType,
              filename: file.name,
              sizeBytes: file.size,
            })),
          ),
        ),
      );
  }

  uploadText(filename: string, contentType: 'text/plain' | 'text/csv', content: string): Observable<KnowledgeUploadedFile> {
    return this.upload(new File([content], filename, { type: contentType }));
  }
}

function normalizeKnowledgeContentType(file: File): string {
  const lowerName = file.name.toLowerCase();

  if (lowerName.endsWith('.md') || lowerName.endsWith('.markdown')) return 'text/markdown';
  if (lowerName.endsWith('.csv')) return 'text/csv';
  if (lowerName.endsWith('.txt')) return 'text/plain';
  if (lowerName.endsWith('.pdf')) return 'application/pdf';
  if (lowerName.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lowerName.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  return file.type || 'text/plain';
}
